import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Button } from "@shadcn-in-nx/ui/button";
import { useTranslation } from "react-i18next";
import { Database, TableIcon, Eye, Pencil } from "lucide-react";

export function DatabaseAccessModal({
    isOpen,
    onClose,
    database,
    table,
    onGrant,
    isGranting,
}) {
    const { t } = useTranslation();
    const [accessType, setAccessType] = useState("read");

    // Reset access type when modal opens
    useEffect(() => {
        if (isOpen) {
            setAccessType("read");
        }
    }, [isOpen]);

    const handleGrant = () => {
        onGrant(accessType);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-blue-100 rounded-full">
                            <Database className="w-5 h-5 text-blue-600" />
                        </div>
                        <DialogTitle>
                            {t("databaseAccess.requestTitle", "Database Access Request")}
                        </DialogTitle>
                    </div>
                    <DialogDescription className="pt-2">
                        {t(
                            "databaseAccess.requestDescription",
                            "You are requesting access to a table from another app's database. Do you want to request access?"
                        )}
                    </DialogDescription>
                </DialogHeader>

                <div className="py-4 space-y-4">
                    <div className="rounded-md border bg-muted/50 p-4 space-y-3">
                        <div className="flex items-center gap-2 text-sm">
                            <Database className="w-4 h-4 text-blue-600" />
                            <span className="text-gray-500">
                                {t("databaseAccess.database", "Database")}:
                            </span>
                            <span className="font-mono bg-background px-1.5 py-0.5 rounded border font-medium">
                                {database?.name}
                            </span>
                        </div>
                        {table && (
                            <div className="flex items-center gap-2 text-sm">
                                <TableIcon className="w-4 h-4 text-green-600" />
                                <span className="text-gray-500">
                                    {t("databaseAccess.table", "Table")}:
                                </span>
                                <span className="font-mono bg-background px-1.5 py-0.5 rounded border font-medium">
                                    {table?.name}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                            {t("databaseAccess.accessLevel", "Access Level")}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={() => setAccessType("read")}
                                className={`flex items-center gap-2 p-3 rounded-md border text-sm transition-colors ${
                                    accessType === "read"
                                        ? "border-blue-500 bg-blue-50 text-blue-700"
                                        : "border-gray-200 hover:border-gray-300"
                                }`}
                            >
                                <Eye className="w-4 h-4" />
                                <div className="text-left">
                                    <div className="font-medium">
                                        {t("databaseAccess.readOnly", "Read")}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        {t("databaseAccess.readOnlyDesc", "View data only")}
                                    </div>
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setAccessType("read-write")}
                                className={`flex items-center gap-2 p-3 rounded-md border text-sm transition-colors ${
                                    accessType === "read-write"
                                        ? "border-blue-500 bg-blue-50 text-blue-700"
                                        : "border-gray-200 hover:border-gray-300"
                                }`}
                            >
                                <Pencil className="w-4 h-4" />
                                <div className="text-left">
                                    <div className="font-medium">
                                        {t("databaseAccess.readWrite", "Read & Write")}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        {t("databaseAccess.readWriteDesc", "View and modify")}
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={isGranting}>
                        {t("common.cancel", "Cancel")}
                    </Button>
                    <Button onClick={handleGrant} disabled={isGranting}>
                        {isGranting
                            ? t("common.requesting", "Requesting...")
                            : t("databaseAccess.requestAccess", "Request Access")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
